import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Modal,
  Button,
  Breadcrumb,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  NavLink,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Base from "../Base";
import PortWiseTab from "./PortWiseTab";
import { TiTick } from "react-icons/ti";
import { CgTranscript } from "react-icons/cg";
import { VscGitFetch } from "react-icons/vsc";
import { ImDownload2 } from "react-icons/im";
import { FaAngleDoubleDown } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import instance from "../../services/baseService";
import useCheckAuthorization from "../../hooks/checkAuthorization";
import { CRUD_PERMISSIONS } from "../../constants/user";
import toast from "react-hot-toast";
import InwardOutwardModal from "./InwardOutwardModal";
import useSidebar from "../../hooks/useSidebar";
import { useDispatch, useSelector } from "react-redux";
import { showLoader } from "../../slices/commonReducer";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import ManualLetterUpload from "./ManualBRC/ManualLetterUpload";
import ManualInwardOutwardModal from "./ManualBRC/ManualInwardOutwardModal";
import { BsInfoCircle } from "react-icons/bs";

function PortWiseReport() {
  const [activeTab, setActiveTab] = useState(null);
  const { client_id, report_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const portCodeIdFromQuery = searchParams.get("portCodeId");
  const [portCodes, setPortCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [reportData, setReportData] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [familyPortcodes, setFamilyPortcodes] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(1);
  const [totalSiblings, setTotalSiblings] = useState(1);
  const [portCodeData, setPortCodeData] = useState({
    gstNo: null,
    all_brc_completed: null,
    scripNo: null,
    scripDate: null,
    iecCode: null,
    status: 0,
    iceGate_approve: null,
    dgft_approve: null,
    portcode_id: null,
    approval_request: 0,
    total_count: 0,
    total_amount: 0,
    scrip_cancelled: 0,
  });
  const approvePermission = useCheckAuthorization(
    CRUD_PERMISSIONS.reconciliation.approve
  );
  const [showInwardOutwardModal, setShowInwardOutwardModal] = useState(false);
  const [showManualInwardOutwardModal, setShowManualInwardOutwardModal] =
    useState(false);
  const [showLetterUploadModal, setShowLetterUploadModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const { collapse, expand, isSidebarCollapsed } = useSidebar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { client: singleClient } = useSelector((state) => state.clients);
  const tabsRef = useRef(null);
  const innerTabsRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 768);
  console.log(tabsRef);
  const scrollTabs = (direction) => {
    if (!tabsRef.current) {
      console.warn("tabsRef is null");
      return;
    }
    tabsRef.current.scrollBy({
      left: direction === "next" ? 200 : -200,
      behavior: "smooth",
    });
  };
  const checkOverflow = () => {
    if (innerTabsRef.current) {
      const { scrollWidth, clientWidth } = innerTabsRef.current;
      setShowArrows(scrollWidth > clientWidth);
    }
  };
  useEffect(() => {
    checkOverflow();
  }, [portCodes]);
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 768);
      checkOverflow();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const handleTabSelect = (newTabId) => {
    const id = parseInt(newTabId) || portCodes[0]?.port_code_id;
    setActiveTab(id);
    setSearchParams({ portCodeId: id }, { replace: true });
    // Reset to parent (order=1) when switching tabs
    setCurrentOrder(1);
    fetchPortCodeData(id);
  };
  useEffect(() => {
    fetchPortCodes();
  }, [client_id, report_id]);
  useEffect(() => {
    if (activeTab && client_id && report_id) {
      // Check if URL has a different portCodeId (could be a child)
      const urlPortCodeId = parseInt(portCodeIdFromQuery);
      if (urlPortCodeId && urlPortCodeId !== activeTab) {
        // URL has a child portCodeId, fetch that data instead
        fetchPortCodeData(urlPortCodeId);
      } else {
        fetchPortCodeData(activeTab);
      }
    }
  }, [activeTab, client_id, report_id]);
  const fetchPortCodes = async () => {
    try {
      setLoading(true);
      const response = await instance.post(
        `/api/v1/reconciliation/port-code/list/${client_id}/${report_id}`
      );
      if (response.data.type === "success") {
        let codes = response.data.data.port_codes || [];
        setPortCodes(codes);
        // Check if current activeTab still exists in the new list
        const activeTabExists = codes.some(
          (code) => code.port_code_id === activeTab
        );
        if (codes.length > 0) {
          if (portCodeIdFromQuery) {
            // First check if it's a parent portCodeId
            const queryPortCodeExists = codes.some(
              (code) => code.port_code_id == portCodeIdFromQuery
            );
            if (queryPortCodeExists) {
              console.log("portCodeIdFromQuery (parent):", portCodeIdFromQuery);
              setActiveTab(parseInt(portCodeIdFromQuery));
            } else {
              // portCodeIdFromQuery might be a child - we'll handle this after fetching data
              // For now, set a temporary flag to indicate we need to resolve the parent
              // The fetchPortCodeData will handle finding the correct parent tab
              setActiveTab(parseInt(portCodeIdFromQuery));
            }
          }
          // Priority 2: Keep existing activeTab if it still exists
          else if (!activeTab || !activeTabExists) {
            // If no active tab or deleted tab, set to first item
            const firstPortCodeId = codes[0].port_code_id;
            setActiveTab(firstPortCodeId);
            setSearchParams({ portCodeId: firstPortCodeId }, { replace: true });
          }
        } else {
          // No codes left, clear everything
          setActiveTab(null);
          setTableData([]);
          setFamilyPortcodes([]);
          setCurrentOrder(1);
          setTotalSiblings(1);
          setPortCodeData({
            gstNo: null,
            all_brc_completed: null,
            scripNo: null,
            scripDate: null,
            iecCode: null,
            status: 0,
            iceGate_approve: null,
            dgft_approve: null,
            portcode_id: null,
            approval_request: 0,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching port codes:", error);
      setPortCodes([]);
      setActiveTab(null);
      setTableData([]);
      setFamilyPortcodes([]);
      setCurrentOrder(1);
      setTotalSiblings(1);
    } finally {
      setLoading(false);
    }
  };
  const fetchPortCodeData = async (portCodeId) => {
    try {
      setLoadingData(true);
      const response = await instance.post(
        `/api/v1/reconciliation/port-code/data/${client_id}/${report_id}`,
        {
          port_code_id: parseInt(portCodeId),
          page: 1,
          limit: 50,
        }
      );
      if (response.data.type === "success") {
        const data = response.data.data;
        setReportData(data.uploaded_file);
        setPortCodeData({
          gstNo: data.gstNo,
          all_brc_completed: data.all_brc_completed,
          scripNo: data.scripNo,
          scripDate: data.scrip_date,
          iecCode: data.iec_code,
          status: data.status,
          iceGate_approve:
            data.iceGate_approve === 0 ? null : data.iceGate_approve,
          dgft_approve: data.dgft_approve === 0 ? null : data.dgft_approve,
          portcode_id: data.portcode_id,
          approval_request: data.approval_request || 0,
          total_count: data.total_count || 0,
          total_amount: data.total_amount || 0,
          scrip_cancelled: data.scrip_cancelled || 0,
        });
        // Set family navigation data
        const family = data.family_portcodes || [];
        setFamilyPortcodes(family);
        setTotalSiblings(family.length);
        // Find current order from family data
        const currentItem = family.find((f) => f.is_current);
        setCurrentOrder(currentItem?.order || 1);
        // If this is a child portCode, ensure the parent tab is highlighted
        // Find the parent (order=1) from family and update activeTab if needed
        if (family.length > 0) {
          const parentPortCode = family.find((f) => f.order === 1);
          if (
            parentPortCode &&
            !portCodes.some((pc) => pc.port_code_id === parseInt(portCodeId))
          ) {
            // Current portCodeId is a child, set activeTab to parent for tab highlighting
            setActiveTab(parentPortCode.port_code_id);
          }
        }
        setTotalPages(data.total_pages || 1);
        const records = data.lists || [];
        const mappedData = records.map((record) => ({
          id: record.id || "",
          sbNo: record.sb_no || "",
          sbDate: record.sb_date || "",
          scrollNo: record.scroll_no || "N/A",
          status: record.status || null,
          type: record.amount_type || "",
          amount: record.amount || "",
          ifsc_code: record.ifsc_code || "",
          brc_data: record.brc_data || [],
        }));
        setTableData(mappedData);
      }
    } catch (error) {
      console.error("Error fetching port code data:", error);
      setTableData([]);
      setFamilyPortcodes([]);
      setCurrentOrder(1);
      setTotalSiblings(1);
    } finally {
      setLoadingData(false);
    }
  };
  // =============================================================================
  // SB NUMBER EXPIRY HIGHLIGHTING LOGIC (Copied from PortWiseTab for parent-level check)
  // Subsidy expires exactly 1 year from sb_date (handles leap years automatically)
  // - 0-180 days elapsed: Normal (no highlight)
  // - 181-299 days elapsed: Warning (light yellow)
  // - 300+ days elapsed (before expiry): Danger (light red)
  // - After expiry date: Expired (light red)
  // =============================================================================
  const WARNING_THRESHOLD = 181; // Days after which warning starts
  const DANGER_THRESHOLD = 300; // Days after which danger starts
  const getExpiryStatus = (sbDate) => {
    // Default safe return for invalid/missing dates
    const defaultReturn = {
      status: "normal",
      daysElapsed: 0,
      daysRemaining: 365,
      expiryDate: null,
      sbDate: null,
    };
    if (!sbDate) return defaultReturn;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time for accurate day calculation
      const sbDateObj = new Date(sbDate);
      // Validate parsed date
      if (isNaN(sbDateObj.getTime())) {
        console.warn("Invalid sb_date:", sbDate);
        return defaultReturn;
      }
      sbDateObj.setHours(0, 0, 0, 0);
      // =============================================================================
      // LEAP YEAR HANDLING:
      // Instead of using a fixed 365 days, we calculate the exact expiry date
      // by adding 1 year to sb_date. JavaScript Date automatically handles leap years.
      // Example:
      // - sb_date: 2024-02-29 → expiry: 2025-02-28 (leap year to non-leap year)
      // - sb_date: 2023-03-01 → expiry: 2024-03-01 (includes Feb 29, 2024)
      // =============================================================================
      const expiryDate = new Date(sbDateObj);
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      // Calculate days elapsed since sb_date
      const msPerDay = 1000 * 60 * 60 * 24;
      const daysElapsed = Math.floor((today - sbDateObj) / msPerDay);
      // Calculate days remaining until expiry (can be negative if expired)
      const daysRemaining = Math.floor((expiryDate - today) / msPerDay);
      // Determine status based on days elapsed and expiry
      let status = "normal";
      if (daysRemaining <= 0) {
        // Already expired
        status = "expired";
      } else if (daysElapsed >= DANGER_THRESHOLD) {
        // 300+ days elapsed, approaching expiry
        status = "danger";
      } else if (daysElapsed >= WARNING_THRESHOLD) {
        // 181-299 days elapsed
        status = "warning";
      }
      return {
        status,
        daysElapsed,
        daysRemaining: Math.max(0, daysRemaining), // Ensure non-negative for display
        expiryDate,
        sbDate: sbDateObj,
      };
    } catch (error) {
      // Catch any unexpected errors to prevent frontend crash
      console.error("Error calculating expiry status:", error);
      return defaultReturn;
    }
  };
  // Compute if any SB in the current tableData is expired
  const isAnySBExpired = useMemo(() => {
    return tableData.some((row) => {
      const expiryInfo = getExpiryStatus(row.sbDate);
      return expiryInfo.status === "expired";
    });
  }, [tableData]);
  const handleApproval = async (
    clientId,
    fileId,
    portCodeId,
    approvalType,
    approve
  ) => {
    try {
      setLoadingData(true);
      const response = await instance.post(
        `/api/v1/reconciliation/port-code/approve/${clientId}/${fileId}/${portCodeId}`,
        {
          approval_type: approvalType, // 1 = IceGate, 2 = DGFT
          approve: approve, // 1 = Approve, 0 = Reject
        }
      );
      toast.success(response.data.message);
      fetchPortCodeData(portCodeId);
    } catch (error) {
      console.error("Approval API Error:", error);
      toast.error(error.response?.data?.message || "Approval failed");
    } finally {
      setLoadingData(false);
    }
  };
  const handleDownloadExcel = async (
    clientId,
    fileId,
    portCodeId,
    downloadBrcExcel
  ) => {
    try {
      setLoadingData(true);
      const response = await instance.post(
        `/api/v1/reconciliation/port-code/excel/download/${clientId}/${fileId}/${portCodeId}`,
        {
          include_brc_data: downloadBrcExcel,
        },
        {
          responseType: "blob",
        }
      );
      const contentDisposition = response.headers["content-disposition"];
      let filename = null;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }
      if (!filename) {
        const clientName = (singleClient?.client_name || "Client").replace(
          /[^a-zA-Z0-9_-]/g,
          "_"
        );
        const scripNo = portCodeData?.scripNo;
        const scripDate = portCodeData?.scripDate
          ? new Date(portCodeData.scripDate)
              .toLocaleDateString("en-GB")
              .replace(/\//g, "")
          : null;
        const filenameParts = [clientName, scripNo, scripDate].filter(Boolean);
        filename = `${filenameParts.join("_")}.xlsx`;
      }
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel file downloaded successfully");
      fetchPortCodeData(portCodeId);
    } catch (error) {
      console.error("Download API Error:", error);
      toast.error(error.response?.data?.message || "Download failed");
    } finally {
      setLoadingData(false);
    }
  };
  const handleSessionGenerate = async (process) => {
    try {
      setLoadingData(true);
      localStorage.removeItem("session_id");
      localStorage.removeItem("process_type");
      const portcodeId = portCodeData.portcode_id;
      const response = await instance.post(
        `/api/v1/reconciliation/session/create/${client_id}/${report_id}`,
        {
          process_type: process,
          portcode_id: portcodeId,
        }
      );
      if (response.data.type === "success") {
        toast.success(response.data.message);
        dispatch(showLoader("Running automation..."));
        // localStorage.setItem('session_id', response.data.data.session_id);
        // localStorage.setItem('process_type', response.data.data.process_type);
        // process === "SCRIP_GENERATION"
        // ? window.open("https://foservices.icegate.gov.in/#/login", "_blank", "noopener")
        // : process === "PROCESS_BRC" && window.open("https://www.dgft.gov.in/CP/", "_blank", "noopener");
      } else {
        toast.error(response.data?.message || "Error Occurred");
      }
    } catch (error) {
      console.error("Error creating automation session:", error);
      const errorMessage =
        error.response?.data?.message || "Failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoadingData(false);
    }
  };
  const handleRequestApproval = async (clientId, reportId, portcodeId) => {
    try {
      setLoadingData(true);
      const response = await instance.post(
        `/api/v1/reconciliation/port-code/approval_request/${clientId}/${reportId}/${portcodeId}`
      );
      toast.success(response.data.message);
      fetchPortCodeData(portcodeId);
    } catch (error) {
      console.error("Approval API Error:", error);
      toast.error(error.response?.data?.message || "Approval failed");
    } finally {
      setLoadingData(false);
    }
  };
  const handlePrevSibling = () => {
    if (currentOrder > 1 && familyPortcodes.length > 0) {
      const prevSibling = familyPortcodes.find(
        (f) => f.order === currentOrder - 1
      );
      if (prevSibling) {
        setSearchParams(
          { portCodeId: prevSibling.port_code_id },
          { replace: true }
        );
        fetchPortCodeData(prevSibling.port_code_id);
      }
    }
  };
  const handleNextSibling = () => {
    if (currentOrder < totalSiblings && familyPortcodes.length > 0) {
      const nextSibling = familyPortcodes.find(
        (f) => f.order === currentOrder + 1
      );
      if (nextSibling) {
        setSearchParams(
          { portCodeId: nextSibling.port_code_id },
          { replace: true }
        );
        fetchPortCodeData(nextSibling.port_code_id);
      }
    }
  };
  const handleClose = () => {
    expand();
    setShowInwardOutwardModal(false);
  };
  const handleNavigateToPortCode = (targetPortCodeId) => {
    setSearchParams({ portCodeId: targetPortCodeId }, { replace: true });
    fetchPortCodeData(targetPortCodeId);
  };
  // Tooltip for expired SB disable reason
  const expiredTooltip = (
    <Tooltip id="expired-tooltip" className="fw-bold">
      Scrip generation is disabled because at least one subsidy has expired.
    </Tooltip>
  );
  return (
    <Base>
      <Container fluid className="p-4 bg-light min-vh-100">
        <Row>
          <Col>
            <Breadcrumb>
              <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/" }}>
                Dashboard
              </Breadcrumb.Item>
              <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/clients" }}>
                Clients
              </Breadcrumb.Item>
              <Breadcrumb.Item
                linkAs={NavLink}
                linkProps={{ to: `/clients/tax-forms/${client_id}` }}
              >
                Tax-Forms
              </Breadcrumb.Item>
              <Breadcrumb.Item
                linkAs={NavLink}
                linkProps={{ to: `/RoDTEP/RoSCTL/${client_id}` }}
              >
                RoDTEP/RoSCTL
              </Breadcrumb.Item>
              <Breadcrumb.Item
                linkAs={NavLink}
                linkProps={{ to: `/RoDTEP/RoSCTL/status/${client_id}` }}
              >
                Upload Status
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => navigate(-1)}
                style={{ cursor: "pointer" }} //
              >
                Reports
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Port Report</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        {/* ======== Card ======== */}
        <Row>
          <Col>
            <div className="bg-white p-4">
              <div className="flex justify-content-between items-center">
                <div>
                  <h1 className="h4 fw-bold mb-2">Highest Value Report</h1>
                  <p className="text-muted small">
                    {reportData.start_date} - {reportData.end_date}
                  </p>
                </div>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : portCodes.length > 0 ? (
                <>
                  <div className="d-flex align-items-center gap-2 mb-4">
                    {(!isLargeScreen || showArrows) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => scrollTabs("prev")}
                        title="Previous"
                      >
                        <FaArrowLeft />
                      </Button>
                    )}
                    <div
                      ref={tabsRef}
                      className="flex-grow-1 overflow-x-auto"
                      style={{ borderBottom: "1px solid #dee2e6" }}
                    >
                      <div ref={innerTabsRef} className="d-flex flex-nowrap">
                        {portCodes.map((portCodeObj, index) => {
                          const id = portCodeObj.port_code_id || index;
                          const title = `Portcode: ${
                            portCodeObj.port_code || portCodeObj
                          }`;
                          const isActive = parseInt(activeTab) === id;
                          return (
                            <div
                              key={id}
                              className="px-3 py-2 border-end"
                              style={{
                                cursor: "pointer",
                                borderBottom: isActive
                                  ? "2px solid #007bff"
                                  : "none",
                                backgroundColor: isActive
                                  ? "#007bff"
                                  : "transparent",
                                color: isActive ? "white" : "#495057",
                                whiteSpace: "nowrap",
                                fontSize: "0.875rem",
                              }}
                              onClick={() => handleTabSelect(id)}
                            >
                              {title}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {(!isLargeScreen || showArrows) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        title="Next"
                        onClick={() => scrollTabs("next")}
                      >
                        <FaArrowRight />
                      </Button>
                    )}
                  </div>
                  <div className="tab-content">
                    {portCodes.map((portCodeObj) => {
                      const id = portCodeObj.port_code_id;
                      if (id !== parseInt(activeTab)) return null;
                      return (
                        <PortWiseTab
                          key={id}
                          portCode={portCodeObj.port_code}
                          portCodeId={portCodeData.portcode_id}
                          clientId={client_id}
                          reportId={report_id}
                          scripNo={portCodeData.scripNo}
                          scripDate={portCodeData.scripDate}
                          gstNo={portCodeData.gstNo}
                          portNo=""
                          iecCode={portCodeData.iecCode}
                          tableHeaders={[
                            "Sl No",
                            "SB NO",
                            "SB Date",
                            "Scroll No",
                            "Status",
                            "Type",
                            "Amount",
                            "FOB",
                            "BRC NO",
                            "BRC Date",
                            "BRC Realised Value",
                            "IFSC Code",
                          ]}
                          tableData={tableData}
                          loading={loadingData}
                          callback={fetchPortCodes}
                          reportData={reportData}
                          totalCount={portCodeData.total_count}
                          totalAmount={portCodeData.total_amount}
                          currentOrder={currentOrder}
                          totalSiblings={totalSiblings}
                          portCodeData={portCodeData}
                          onNavigateToPortCode={handleNavigateToPortCode}
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted">
                    No port codes found for this period
                  </p>
                </div>
              )}
              {portCodes.length > 0 && (
                <div className="flex flex-col md:flex-row justify-between items-center mt-4 gap-2 ">
                  {/* Pagination Section */}
                  <div className="d-flex align-items-center gap-2">
                    {totalSiblings > 1 && (
                      <>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handlePrevSibling}
                          disabled={currentOrder === 1 || loadingData}
                          title="Previous Part"
                        >
                          <FaArrowLeft />
                        </Button>
                        <span className="text-muted small fw-medium">
                          Part {currentOrder} of {totalSiblings}
                        </span>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleNextSibling}
                          disabled={
                            currentOrder === totalSiblings || loadingData
                          }
                          title="Next Part"
                        >
                          <FaArrowRight />
                        </Button>
                      </>
                    )}
                    {portCodeData.scrip_cancelled === 1 && (
                      <div className="px-3 py-2 rounded-pill fw-bold !text-red-700 !bg-red-100">
                        Scrip was once cancelled
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2 flex-col md:flex-row justify-start">
                    <Button
                      variant="primary"
                      className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                      onClick={() =>
                        handleDownloadExcel(
                          client_id,
                          report_id,
                          portCodeData.portcode_id,
                          portCodeData.status === 0 ? false : true
                        )
                      }
                      disabled={loadingData}
                    >
                      <ImDownload2 />
                      Download Excel
                    </Button>
                    {/* ========== STATUS 0, 10, OR 3: Approval Stages ========== */}
                    {(portCodeData.status === 0 ||
                      portCodeData.status === 10 ||
                      portCodeData.status === 3) && (
                      <>
                        {(() => {
                          const isIceGateStage = portCodeData.status === 0;
                          const isDGFTStage = portCodeData.status === 3;
                          const isScripStage = portCodeData.status === 10;
                          const currentApproval = isIceGateStage
                            ? portCodeData.iceGate_approve
                            : isScripStage
                            ? portCodeData.scrip_approve // NEW
                            : portCodeData.dgft_approve;
                          const approvalType = isIceGateStage
                            ? 1
                            : isScripStage
                            ? 2
                            : 3;
                          const downloadBrcExcel = isIceGateStage
                            ? false
                            : true;
                          // Case 1: Director Rejected (Show for both users)
                          if (currentApproval === 2) {
                            return (
                              <div
                                className="px-3 py-2 rounded-pill fw-bold text-white"
                                style={{ backgroundColor: "#dc3545" }}
                              >
                                Rejected
                              </div>
                            );
                          }
                          // Case 2: No approval request yet (Show "Request Approval" for both users)
                          if (portCodeData.approval_request === 0) {
                            return (
                              <>
                                {/* <Button
                                  variant="primary"
                                  className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                                    onClick={() => handleDownloadExcel(
                                      client_id,
                                      report_id,
                                      portCodeData.portcode_id,
                                      downloadBrcExcel
                                                                    )}
                                  disabled={loadingData}
                                >
                                  <ImDownload2 />
                                  Download Excel
                                </Button> */}
                                <Button
                                  variant="primary"
                                  className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                  onClick={() =>
                                    handleRequestApproval(
                                      client_id,
                                      report_id,
                                      portCodeData.portcode_id
                                    )
                                  }
                                  disabled={loadingData}
                                >
                                  <TiTick />
                                  Request Approval (Part {currentOrder} of {totalSiblings})
                                </Button>
                              </>
                            );
                          }
                          // Case 3: Approval requested (approval_request = 1)
                          if (portCodeData.approval_request === 1) {
                            // Users WITHOUT approve permission: Show waiting message
                            if (!approvePermission) {
                              return (
                                <div
                                  className="px-3 py-2 rounded-pill fw-bold text-yellow-700"
                                  style={{ backgroundColor: "#fff3cd" }}
                                >
                                  Waiting for Approval
                                </div>
                              );
                            }
                            // Users WITH approve permission: Show Approve/Reject buttons
                            return (
                              <>
                                {/* <Button
                                  variant="primary"
                                  className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                                                    onClick={() => handleDownloadExcel(
                                      client_id,
                                      report_id,
                                      portCodeData.portcode_id,
                                      downloadBrcExcel
                                                                    )}
                                  disabled={loadingData}
                                >
                                  <ImDownload2 />
                                  Download Excel
                                </Button> */}
                                <Button
                                  variant="success"
                                  className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                  onClick={() =>
                                    handleApproval(
                                      client_id,
                                      report_id,
                                      portCodeData.portcode_id,
                                      approvalType,
                                      1 // Approve
                                    )
                                  }
                                  disabled={loadingData}
                                >
                                  <TiTick />
                                  Approve (Part {currentOrder} of {totalSiblings})
                                </Button>
                                <Button
                                  variant="danger"
                                  className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                                  onClick={() =>
                                    handleApproval(
                                      client_id,
                                      report_id,
                                      portCodeData.portcode_id,
                                      approvalType,
                                      2 // Reject
                                    )
                                  }
                                  disabled={loadingData}
                                >
                                  <TiTick />
                                  Reject (Part {currentOrder} of {totalSiblings})
                                </Button>
                              </>
                            );
                          }
                          return null;
                        })()}
                      </>
                    )}
                    {/* ========== STATUS 1: Scrip Generation ========== */}
                    {portCodeData.status === 1 && (
                      <OverlayTrigger
                        placement="top"
                        overlay={isAnySBExpired ? expiredTooltip : <></>}
                      >
                        <span>
                          <Button
                            variant="primary"
                            className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                            onClick={() =>
                              handleSessionGenerate("SCRIP_GENERATION")
                            }
                            disabled={loadingData || isAnySBExpired}
                          >
                            <CgTranscript />
                            {loadingData
                              ? "Processing..."
                              : `Scrip Generate (Part ${currentOrder} of ${totalSiblings})`}
                          </Button>
                        </span>
                      </OverlayTrigger>
                    )}
                    {/* ========== STATUS 11: BRC Processing ========== */}
                    {[4, 11].includes(portCodeData.status) &&
                      portCodeData.all_brc_completed === false && (
                        <Button
                          variant="primary"
                          className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                          onClick={() => handleSessionGenerate("PROCESS_BRC")}
                          disabled={loadingData}
                        >
                          <VscGitFetch />
                          {loadingData ? "Processing..." : "Fetch BRC"}
                        </Button>
                      )}
                    {/* ========== STATUSES 4: Generate Letter ========== */}
                    {[4, 11].includes(portCodeData.status) &&
                      reportData.manual_brc === 0 && (
                        <>
                          <Button
                            variant="primary"
                            className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                            disabled={loadingData}
                            onClick={() => {
                              setShowInwardOutwardModal(true);
                              collapse();
                            }}
                          >
                            <FaAngleDoubleDown />
                            View or Generate Transfer Letters
                          </Button>
                        </>
                      )}
                    {[4, 11].includes(portCodeData.status) &&
                      reportData.manual_brc === 1 && (
                        <>
                          <Button
                            variant="primary"
                            className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                            disabled={loadingData}
                            onClick={() => {
                              setShowLetterUploadModal(true);
                              collapse();
                            }}
                          >
                            <FiUpload className="me-2" />
                            Upload Transfer Letters
                          </Button>
                          <Button
                            variant="primary"
                            className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                            disabled={loadingData}
                            onClick={() => {
                              setShowManualInwardOutwardModal(true);
                              collapse();
                            }}
                          >
                            <FaAngleDoubleDown />
                            View Transfer Letters
                          </Button>
                        </>
                      )}
                    {/* ========== STATUS 5: Rejected (Terminal State) ========== */}
                    {portCodeData.status === 5 && (
                      <div
                        className="px-3 py-2 rounded-pill fw-bold text-white"
                        style={{ backgroundColor: "#dc3545" }}
                      >
                        Workflow Rejected
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
        <InwardOutwardModal
          show={showInwardOutwardModal}
          onHide={() => handleClose()}
          clientId={client_id}
          reportId={report_id}
          port_code_id={portCodeData.portcode_id}
        />
        <ManualInwardOutwardModal
          show={showManualInwardOutwardModal}
          onHide={() => setShowManualInwardOutwardModal(false)}
          clientId={client_id}
          reportId={report_id}
          port_code_id={portCodeData.portcode_id}
        />
        <ManualLetterUpload
          show={showLetterUploadModal}
          onHide={() => setShowLetterUploadModal(false)}
          clientId={client_id}
          reportId={report_id}
          port_code_id={portCodeData.portcode_id}
          reFetchcallback={() => fetchPortCodeData(portCodeData.portcode_id)}
        />
      </Container>
    </Base>
  );
}
export default PortWiseReport;
